import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Clock,
  Loader2,
  Activity,
  CheckCircle2,
  XCircle,
  Info,
  RefreshCw,
} from 'lucide-react';
import { getExpiringStock, getRecentActivity, getSystemAlerts } from '../../lib/api';

const ACTION_ICON = {
  create: CheckCircle2,
  update: RefreshCw,
  delete: XCircle,
  login: Activity,
  failed: AlertTriangle,
};

const SEVERITY_STYLE = {
  error:    'bg-red-50 border-red-300 text-red-800',
  warning:  'bg-amber-50 border-amber-300 text-amber-800',
  info:     'bg-blue-50 border-blue-300 text-blue-800',
};

const URGENCY_STYLE = {
  critical: 'text-red-700 bg-red-50',
  warning:  'text-amber-700 bg-amber-50',
  info:     'text-zinc-600 bg-zinc-50',
};

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000)       return 'just now';
    if (ms < 3_600_000)    return `${Math.floor(ms / 60_000)} min ago`;
    if (ms < 86_400_000)   return `${Math.floor(ms / 3_600_000)} h ago`;
    if (ms < 7 * 86_400_000) return `${Math.floor(ms / 86_400_000)} d ago`;
    return new Date(iso).toLocaleDateString('en-IE');
  } catch {
    return iso;
  }
}

function actionLabel(action) {
  if (!action) return '';
  return String(action).replace(/_/g, ' ');
}

export default function DashboardLiveWidgets() {
  const [alerts, setAlerts] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [a, e, r] = await Promise.all([
        getSystemAlerts(),
        getExpiringStock(14),
        getRecentActivity(8),
      ]);
      setAlerts(a.data?.alerts || []);
      setExpiring(e.data?.batches || []);
      setActivity(r.data?.activities || []);
    } catch {
      // swallow — widgets are best-effort, not blocking the dashboard
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 60_000); // auto-refresh every 60s
    return () => clearInterval(t);
  }, [loadAll]);

  if (loading) {
    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="h-[260px] flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="dashboard-live-widgets">
      {/* ── System Alerts ── */}
      <Card data-testid="widget-alerts">
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="w-4 h-4" />
            System Alerts
            {alerts.length > 0 && (
              <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">{alerts.length}</Badge>
            )}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={loadAll} title="Refresh">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0 pb-3 space-y-2 max-h-[230px] overflow-y-auto">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
              <CheckCircle2 className="w-7 h-7 mb-1.5 text-emerald-400" />
              <p className="text-xs">No alerts. All clear.</p>
            </div>
          ) : (
            alerts.map((a, i) => {
              const cls = SEVERITY_STYLE[a.severity] || SEVERITY_STYLE.info;
              const inner = (
                <div className={`border rounded-md px-3 py-2 text-xs ${cls}`} data-testid={`alert-${a.type}`}>
                  <div className="flex items-start gap-2">
                    {a.severity === 'error' ? <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      : a.severity === 'warning' ? <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      : <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold leading-tight">{a.title}</p>
                      <p className="opacity-80 leading-tight mt-0.5">{a.message}</p>
                    </div>
                    {a.action_url && <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />}
                  </div>
                </div>
              );
              return a.action_url
                ? <Link key={i} to={a.action_url} className="block hover:opacity-90">{inner}</Link>
                : <div key={i}>{inner}</div>;
            })
          )}
        </CardContent>
      </Card>

      {/* ── Expiring Stock (14 days) ── */}
      <Card data-testid="widget-expiring-stock">
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Expiring Stock
            <span className="text-[10px] text-muted-foreground font-normal">(14 days)</span>
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/batches" className="text-xs">
              <ArrowRight className="w-3 h-3" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0 pb-3 max-h-[230px] overflow-y-auto">
          {expiring.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
              <CheckCircle2 className="w-7 h-7 mb-1.5 text-emerald-400" />
              <p className="text-xs">No batches expiring soon.</p>
            </div>
          ) : (
            <div className="divide-y">
              {expiring.slice(0, 8).map((b) => {
                const ucls = URGENCY_STYLE[b.urgency] || URGENCY_STYLE.info;
                const days = b.days_until_expiry;
                return (
                  <Link
                    key={b.id}
                    to={`/batches?search=${encodeURIComponent(b.batch_code || '')}`}
                    className="flex items-center justify-between py-1.5 hover:bg-muted/40 px-1 rounded text-xs"
                    data-testid={`expiring-batch-${b.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-mono font-medium truncate">{b.batch_code}</p>
                      <p className="text-muted-foreground truncate">{b.product_name || '—'}</p>
                    </div>
                    <div className="text-right shrink-0 ml-2">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${ucls}`}>
                        {days <= 0 ? 'EXPIRED' : `${days}d`}
                      </span>
                      <p className="text-[9px] text-muted-foreground mt-0.5">
                        {(b.quantity_available || 0).toFixed(0)} units
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Recent Activity ── */}
      <Card data-testid="widget-recent-activity">
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Recent Activity
          </CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/compliance" className="text-xs">
              <ArrowRight className="w-3 h-3" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-0 pb-3 max-h-[230px] overflow-y-auto">
          {activity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-6 text-muted-foreground">
              <Activity className="w-7 h-7 mb-1.5 opacity-40" />
              <p className="text-xs">No recent activity.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {activity.map((a, i) => {
                const Icon = ACTION_ICON[String(a.action || '').split('_')[0]] || Activity;
                return (
                  <div
                    key={a.id || i}
                    className="flex items-start gap-2 text-xs px-1 py-1 rounded hover:bg-muted/40"
                    data-testid={`activity-${i}`}
                  >
                    <Icon className="w-3 h-3 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="font-medium truncate">{a.user_email || 'system'}</span>
                        <span className="text-muted-foreground">{actionLabel(a.action)}</span>
                      </div>
                      <p className="text-muted-foreground truncate">
                        {(a.entity_type || '').replace(/_/g, ' ')}
                        {a.entity_id ? ` · ${String(a.entity_id).slice(0, 8)}…` : ''}
                      </p>
                    </div>
                    <span className="text-[9px] text-muted-foreground whitespace-nowrap shrink-0">
                      {timeAgo(a.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
